/**
 * --------------------------------------------------------------------------
 * Hive Component: General.js
 * Copyright (c) 2005 - 2022 Paul Höcker / PHMG All rights reserved.
 * Licensed under GNU General Public License version 2 or later
 * --------------------------------------------------------------------------
 */

// Reference
//let utils;

/**
 * Extends joomlas showOn script with BS collapse, toggles required fields
 */
const ShowOnExtension = {
  initiated : false,
  enabled   : false,

  bs : null,


  idx        : 0,
  stateIndex : {},

  /**
   * Index for storing required elements
   */
  requiredFields    : {},
  dependantElements : {},
  blockedElements   : [],

  /**
   * Get internal index from element
   * @param {HTMLElement} element
   * @return {string}
   */
  getIndex(element) {
    let index = element.getAttribute('data-element-idx') || null;
    if (!index) {
      index = `el${this.idx}`;
      ++this.idx;
      element.setAttribute('data-element-idx', index);
    }
    return index;
  },

  /**
   * Get an element by its index
   * @param {string} index
   * @returns {HTMLElement|null}
   */
  getElement(index) {
    return document.querySelector(`[data-element-idx="${index}"]`);
  },

  /**
   * Keep track of the state
   * @param {(HTMLElement|string)} element
   * @param {(-1|1)} state
   */
  setState(element, state) {
    let index = typeof element === 'string' ? element : this.getIndex(element);

    this.stateIndex[index] = state;
  },

  /**
   * Get the current show on state. Showon has to be initialized.
   * @param {(HTMLElement|string)} element
   * @returns {boolean|null}
   */
  getState(element) {
    let index;
    if (typeof element === 'string') {
      index = element;
      element = this.getElement(index);
    } else {
      index = this.getIndex(element);
    }
    let value = this.stateIndex[index] || 0;

    if (value === 0 && !element.hasAttribute('data-showon-initialised')) return null;

    return value > 0 ? true : (value < 0 ? false : !element.classList.contains('hidden'));
  },

  /**
   * Set required attribute
   * @param {(HTMLElement|string)} element
   * @param {boolean} [require]
   */
  setRequired(element, require) {
    let index = typeof element === 'string' ? element : this.getIndex(element);

    // Bail if there are no fields to require or required state is disabled
    const fields = this.requiredFields[index] || []
    if (!fields.length || this.blockedElements.includes(index)) return;

    // Get require from state
    require = typeof require === "boolean" ? require : this.getState(element);
    if (require === null) return;

    // Set fields
    fields.forEach(field => this.setFieldRequired(field, require));

    // Prohibit dependant elements from changing to required if this element is not
    let dependants = this.dependantElements[index] || [];
    if (dependants.length) {
      if (!require) {
        this.blockedElements = Hive.dataInterface.merge(this.blockedElements, dependants);
      } else {
        this.blockedElements = this.blockedElements.filter(idx => !dependants.includes(idx));
      }
    }
  },

  /**
   * Set required attribute
   * @param {HTMLElement} field
   * @param {boolean} require
   */
  setFieldRequired(field, require) {
    if (require) {
      field.required = true;
      field.classList.add('required')
      field.removeAttribute('data-required-suppressed')
    } else {
      field.required = false;
      field.classList.remove('required')
      field.setAttribute('data-required-suppressed', '')
    }
  },

  /**
   * Set collapse state
   * @param {HTMLElement} element
   */
  setShowHide(element) {
    let show = this.getState(element);
    if (show === null) return;

    let inst = this.bs.Collapse.getOrCreateInstance(element, { toggle : false });
    if (show) inst.show();
    else inst.hide();
  },

  /**
   * Find required fields inside this element
   * @param {HTMLElement} element
   */
  initRequiredFields(element) {
    let index = this.getIndex(element);

    this.requiredFields[index] = [].slice.call(element.querySelectorAll('[required],[data-required-suppressed]'));

    if (this.requiredFields[index].length) {
      // Check if there are other showon elements inside this one
      // and add them to the dependants list
      this.dependantElements[index] = [];
      this.getShowOnElements(element).forEach(dependant => this.dependantElements[index].push(this.getIndex(dependant)))

      // Set the required attributes
      this.setRequired(element);
    }
  },

  /**
   * Get showon elements
   * @param {(HTMLElement|Document)} container
   * @returns {HTMLElement[]}
   */
  getShowOnElements : container => [].slice.call(container.querySelectorAll('[data-showon]')),

  /**
   * Restore required attribute
   * @param {(HTMLElement|Document)} container
   * @param {boolean} enable
   */
  onValidationChange(container, enable) {
    if (!this.enabled) return;

    const elements = this.getShowOnElements(container);
    if (!elements.length) return;

    elements.forEach(element => {
      if (enable) {
        // Re-initiate validation
        this.initRequiredFields(element);
      } else {
        // Disable validation
        this.setRequired(element, false);
        // Unset fields index
        let index = this.getIndex(element);
        this.requiredFields[index] = [];
      }
    })
  },

  /**
   * Initiate bs collapse and required fields
   * @param {(HTMLElement|Document)} container
   * */
  addExtension(container) {
    const elements = this.getShowOnElements(container);
    if (!elements.length) return;

    // Set the bootstrap instance
    this.bs = this.bs || window.bootstrap
    this.enabled = true;

    elements.forEach(
      element => {
        if (element.tagName !== 'option'/* && !element.classList.contains('collapse')*/) {

          // If showon hasn't initialized yet or the element isn't hidden
          this.setShowHide(element);

          // Add the fade class afterward, so we don't trigger the collapse event
          element.classList.add('fade');
        }

        // Initiate required fields of this element
        this.initRequiredFields(element);
      });
  },

  /**
   * Find readonly selects
   * @param {(HTMLElement|Document)} container
   * @returns {Promise<boolean>}
   */
  findDisabledSelects : async (container) => {
    // Reset the id of readonly select fields
    let reset = false;
    const disabledFields = [].slice.call(container.querySelectorAll('select[disabled] + input[type=hidden]'));
    if (disabledFields.length) {
      let fieldNames = [];

      // Wait until we got all fields
      disabledFields.forEach(
        field => {
          const select = field.previousElementSibling;
          let fieldId = select.id;
          select.id = `${fieldId}_disabled`;
          field.id = fieldId;

          fieldNames.push(field.name);
        })

      // Here we can bail on the first found element
      const initiatedFields = [].slice.call(container.querySelectorAll('[data-showon-initialised]'));
      if (initiatedFields.length) {
        initiatedFields.forEach(
          field => {
            const jsondata = field.getAttribute('data-showon') || '';
            const showonData = JSON.parse(jsondata);
            if (showonData.length && fieldNames.includes(showonData[0]["field"])) {
              reset = true;
            }
          })
      }
    }

    return reset;
  },

  /**
   * Reset the extension
   */
  resetExtension() {
    // Remove marker
    const elements = this.getShowOnElements(document);
    elements.forEach(element => element.removeAttribute('data-showon-initialised'))

    // Init again
    const Joomla = window.Joomla;
    Joomla.Showon.initialise(document);
  },

  /**
   * Add showon extension
   */
  init() {

    // Set link to utils
    //utils = Hive.getUtils();

    // Native: when showon fails
    const onInitError = (event) => {
      if (event.filename.indexOf('showon.') > -1) {
        window.removeEventListener("error", onInitError);
        this.findDisabledSelects(document)
          .then(reset => {
            if (reset) this.resetExtension();
            if (this.enabled) this.addExtension(document)
          })
        return true;
      }
    }

    window.addEventListener("error", onInitError);

    // Joomla Showon: Extend on show, restore required
    document.addEventListener("joomla:showon-show", ({ target : element }) => {
      this.setState(element, 1);
      if (this.enabled) {
        this.setShowHide(element);
        this.setRequired(element);
      }
    });
    // Joomla Showon: Collapse on hide, remove required
    document.addEventListener("joomla:showon-hide", ({ target : element }) => {
      this.setState(element, -1);
      if (this.enabled) {
        this.setShowHide(element);
        this.setRequired(element);
      }
    });

    // Custom: when required handling is disabled
    document.addEventListener(Hive.eventInterface.getEventKey('FORM_VALIDATION_DISABLED'), ({ target }) => {
      this.onValidationChange(target, false)
    });
    // Custom: when required handling is enabled
    document.addEventListener(Hive.eventInterface.getEventKey('FORM_VALIDATION_ENABLED'), ({ target }) => {
      this.onValidationChange(target, true)
    });

    // Joomla dom update: Update fields
    document.addEventListener(Hive.eventInterface.getEventKey('DOM_CHANGE'), ({ target }) => {
      this.addExtension(target)
    });

    // Native: Run when the document is ready
    if (document.readyState === "complete" || document.readyState === "loaded" || document.readyState === "interactive") {
      window.removeEventListener("error", onInitError);
      this.addExtension(document);
    } else {
      document.addEventListener("DOMContentLoaded", ({ target }) => {
        window.removeEventListener("error", onInitError);
        this.addExtension(target)
      });
    }

    this.initiated = true;

    return this;
  }
}

//noinspection JSUnusedGlobalSymbols
export default ShowOnExtension;
